// client/src/pages/Admin/ProjectManager/components/ProjectGrid.tsx
/**
 * PROJECT GRID COMPONENT
 * 
 * Renders a grid of project cards.
 * 
 * @module ProjectManager/Components/ProjectGrid
 */

import React from 'react';
import { Project } from '../types/project.types';
import { ProjectStatus } from '../../../../shared/models/Project';
import { ProjectCard } from './ProjectCard';
import { ProjectEmptyState } from './ProjectEmptyState';

interface ProjectGridProps {
    projects: Project[];
    selectedIds: Set<string>;
    onToggleSelection: (id: string) => void;
    onEdit: (project: Project) => void;
    onDelete: (id: string) => void;
    onStatusChange: (id: string, status: ProjectStatus) => void;
    onToggleActionMenu: (id: string, e: React.MouseEvent) => void;
    openActionMenu: string | null;
    getMatchFields: (project: Project) => string[];
    debouncedSearchTerm: string;
    hasActiveFilters?: boolean;
}

export const ProjectGrid: React.FC<ProjectGridProps> = ({
    projects,
    selectedIds,
    onToggleSelection,
    onEdit,
    onDelete,
    onStatusChange,
    onToggleActionMenu,
    openActionMenu,
    getMatchFields,
    debouncedSearchTerm,
    hasActiveFilters = false
}) => {
    // Safety check for projects array
    const safeProjects = Array.isArray(projects) ? projects : [];

    if (safeProjects.length === 0) {
        return (
            <ProjectEmptyState
                hasActiveFilters={hasActiveFilters}
                onNewProject={() => {}}
            />
        );
    }

    return (
        <div className="project-grid">
            <div className="project-grid-header">
                <span className="project-count">
                    Showing {safeProjects.length} project{safeProjects.length !== 1 ? 's' : ''}
                </span>
            </div>

            <div className="project-list">
                {safeProjects.map((project) => {
                    if (!project._id) return null;

                    const matchFields = getMatchFields(project);

                    return (
                        <ProjectCard
                            key={project._id}
                            project={project}
                            isSelected={selectedIds.has(project._id)}
                            onToggleSelection={onToggleSelection}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onStatusChange={onStatusChange}
                            onToggleActionMenu={onToggleActionMenu}
                            isActionMenuOpen={openActionMenu === project._id}
                            matchFields={matchFields}
                            debouncedSearchTerm={debouncedSearchTerm}
                        />
                    );
                })}
            </div>
        </div>
    );
};